import { createAxiosInstance } from '../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Create axios instance with default config and interceptors
const orderAPI = createAxiosInstance({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
orderAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    console.error('API Error:', errorMessage);
    return Promise.reject(errorMessage);
  }
);

export const orderService = {
  // Get all orders for a customer with filters
  getCustomerOrders: async (customerId, params = {}) => {
    try {
      const { page = 0, size = 10, status, search } = params;
      const queryParams = { page, size };
      
      if (status && status !== 'all') {
        queryParams.status = status.toUpperCase();
      }
      
      if (search) {
        queryParams.search = search;
      }

      const response = await orderAPI.get(`/orders/customer/${customerId}`, {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch customer orders');
    }
  },

  // Get order by ID
  getOrderById: async (orderId) => {
    try {
      const response = await orderAPI.get(`/orders/${orderId}`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch order details');
    }
  },

  // Track order
  trackOrder: async (orderId) => {
    try {
      const response = await orderAPI.get(`/orders/${orderId}/tracking`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to track order');
    }
  },

  // Cancel order
  cancelOrder: async (orderId, reason) => {
    try {
      const response = await orderAPI.post(`/orders/${orderId}/cancel`, { reason });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to cancel order');
    }
  },

  // Update order status (admin function)
  updateOrderStatus: async (orderId, status, reason = '') => {
    try {
      const response = await orderAPI.put(`/orders/${orderId}/status`, {
        status: status.toUpperCase(),
        reason
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to update order status');
    }
  },

  // Get order analytics
  getOrderAnalytics: async () => {
    try {
      const response = await orderAPI.get('/orders/analytics');
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch order analytics');
    }
  },

  // Get pending approval orders (admin only)
  getPendingApprovalOrders: async (params = {}) => {
    try {
      const { page = 0, size = 20, sortBy = 'createdAt', sortDir = 'desc' } = params;
      const response = await orderAPI.get('/orders/admin/pending-approval', {
        params: { page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch pending approval orders');
    }
  },

  // Approve order (admin only)
  approveOrder: async (orderId, approvedBy) => {
    try {
      const response = await orderAPI.post(`/orders/admin/${orderId}/approve`, {
        approvedBy: approvedBy || 'admin'
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to approve order');
    }
  },

  // Reject order (admin only)
  rejectOrder: async (orderId, rejectionReason, rejectedBy) => {
    try {
      const response = await orderAPI.post(`/orders/admin/${orderId}/reject`, {
        rejectionReason,
        rejectedBy: rejectedBy || 'admin'
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to reject order');
    }
  },

  // Get orders by status
  getOrdersByStatus: async (status, params = {}) => {
    try {
      const { page = 0, size = 20 } = params;
      const response = await orderAPI.get(`/orders/status/${status.toUpperCase()}`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      throw new Error(error || `Failed to fetch ${status} orders`);
    }
  },

  // Search orders
  searchOrders: async (searchTerm, params = {}) => {
    try {
      const { page = 0, size = 10, customerId } = params;
      const queryParams = { page, size, search: searchTerm };
      
      if (customerId) {
        queryParams.customerId = customerId;
      }

      const response = await orderAPI.get('/orders/search', {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to search orders');
    }
  },

  // Create order
  createOrder: async (orderData) => {
    try {
      const response = await orderAPI.post('/orders', orderData);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to create order');
    }
  },

  // Ship order
  shipOrder: async (orderId, trackingData) => {
    try {
      const response = await orderAPI.post(`/orders/${orderId}/ship`, trackingData);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to ship order');
    }
  },

  // Deliver order
  deliverOrder: async (orderId) => {
    try {
      const response = await orderAPI.post(`/orders/${orderId}/deliver`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to deliver order');
    }
  },

  // Add order note
  addOrderNote: async (orderId, note) => {
    try {
      const response = await orderAPI.post(`/orders/${orderId}/notes`, { note });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to add order note');
    }
  },

  // Get order notes
  getOrderNotes: async (orderId) => {
    try {
      const response = await orderAPI.get(`/orders/${orderId}/notes`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch order notes');
    }
  },

  // Process payment
  processPayment: async (orderId, paymentData) => {
    try {
      const response = await orderAPI.post(`/orders/${orderId}/payment`, paymentData);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to process payment');
    }
  },

  // Request refund
  requestRefund: async (orderId, refundData) => {
    try {
      const response = await orderAPI.post(`/orders/${orderId}/refund`, refundData);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to request refund');
    }
  },

  // Get order statistics for dashboard
  getOrderStatistics: async (customerId) => {
    try {
      const response = await orderAPI.get(`/orders/customer/${customerId}/statistics`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch order statistics');
    }
  },

  // Export orders
  exportOrders: async (params = {}) => {
    try {
      const response = await orderAPI.get('/orders/export', {
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to export orders');
    }
  },

  // ===== SELLER-SPECIFIC METHODS =====

  // Get all orders for a seller with filters and pagination
  getSellerOrders: async (sellerId, params = {}) => {
    try {
      const { page = 0, size = 10, status, search, sortBy = 'createdAt', sortDir = 'desc' } = params;
      const queryParams = { page, size, sortBy, sortDir };
      
      if (status && status !== 'all') {
        queryParams.status = status.toUpperCase();
      }
      
      if (search) {
        queryParams.search = search;
      }

      const response = await orderAPI.get(`/orders/seller/${sellerId}`, {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch seller orders');
    }
  },

  // Get seller order analytics and statistics
  getSellerOrderAnalytics: async (sellerId) => {
    try {
      const response = await orderAPI.get(`/orders/seller/${sellerId}`, {
        params: { page: 0, size: 1 } // Get analytics from the response
      });
      return response.data.analytics || {};
    } catch (error) {
      throw new Error(error || 'Failed to fetch seller order analytics');
    }
  },

  // Confirm order (seller action)
  confirmOrder: async (orderId) => {
    try {
      const response = await orderAPI.post(`/orders/${orderId}/confirm`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to confirm order');
    }
  },

  // Get filtered orders for seller with advanced filtering
  getFilteredSellerOrders: async (sellerId, filters = {}) => {
    try {
      const { 
        status, 
        customerSearch, 
        dateRange,
        page = 0, 
        size = 10, 
        sortBy = 'createdAt', 
        sortDir = 'desc' 
      } = filters;
      
      const queryParams = { page, size, sortBy, sortDir };
      
      if (status && status !== 'all') {
        if (Array.isArray(status)) {
          queryParams.status = status.join(',');
        } else {
          queryParams.status = status.toUpperCase();
        }
      }
      
      if (customerSearch && customerSearch.trim()) {
        queryParams.search = customerSearch.trim();
      }

      if (dateRange && dateRange !== 'all') {
        queryParams.dateRange = dateRange;
      }

      const response = await orderAPI.get(`/orders/seller/${sellerId}`, {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch filtered seller orders');
    }
  },

  // Get real-time order counts for seller dashboard
  getSellerOrderCounts: async (sellerId) => {
    try {
      // Get orders with minimal data to count by status
      const response = await orderAPI.get(`/orders/seller/${sellerId}`, {
        params: { page: 0, size: 1000 } // Get all orders for counting
      });
      
      const orders = response.data.orders || [];
      const analytics = response.data.analytics || {};
      
      // Calculate counts by status
      const counts = {
        total: orders.length,
        pending: orders.filter(order => order.status === 'PENDING').length,
        confirmed: orders.filter(order => order.status === 'CONFIRMED').length,
        processing: orders.filter(order => order.status === 'PROCESSING').length,
        shipped: orders.filter(order => order.status === 'SHIPPED').length,
        delivered: orders.filter(order => order.status === 'DELIVERED').length,
        cancelled: orders.filter(order => order.status === 'CANCELLED').length,
        ...analytics
      };
      
      return counts;
    } catch (error) {
      throw new Error(error || 'Failed to fetch seller order counts');
    }
  },

  // ===== ADMIN-SPECIFIC METHODS =====

  // Get all orders for admin with filters and pagination
  getAllOrdersForAdmin: async (params = {}) => {
    try {
      const { page = 0, size = 10, status, search, sellerId, customerId, sortBy = 'createdAt', sortDir = 'desc' } = params;
      const queryParams = { page, size, sortBy, sortDir };
      
      if (status && status !== 'all') {
        queryParams.status = status.toUpperCase();
      }
      
      if (search) {
        queryParams.search = search;
      }

      if (sellerId) {
        queryParams.sellerId = sellerId;
      }

      if (customerId) {
        queryParams.customerId = customerId;
      }

      const response = await orderAPI.get('/orders/debug/all', {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch all orders for admin');
    }
  },

  // Get admin order analytics
  getAdminOrderAnalytics: async () => {
    try {
      const response = await orderAPI.get('/orders/analytics');
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch admin order analytics');
    }
  },

  // Get order counts by status for admin dashboard
  getAdminOrderCounts: async () => {
    try {
      const response = await orderAPI.get('/orders/debug/all');
      const orders = response.data.orders || [];
      
      // Calculate counts by status
      const counts = {
        total: orders.length,
        pending: orders.filter(order => order.status === 'PENDING').length,
        confirmed: orders.filter(order => order.status === 'CONFIRMED').length,
        processing: orders.filter(order => order.status === 'PROCESSING').length,
        shipped: orders.filter(order => order.status === 'SHIPPED').length,
        delivered: orders.filter(order => order.status === 'DELIVERED').length,
        cancelled: orders.filter(order => order.status === 'CANCELLED').length,
        refunded: orders.filter(order => order.status === 'REFUNDED').length
      };
      
      return counts;
    } catch (error) {
      throw new Error(error || 'Failed to fetch admin order counts');
    }
  }
};

export default orderService;